import type { MarkedElement } from '../shared/types.ts';

const TEST_ATTRS = ['data-testid', 'data-test', 'data-qa', 'data-cy', 'name', 'aria-label'];
// Generated ids / class names (React useId, Ember, CSS-in-JS hashes …) make poor selectors.
const UNSTABLE = /\d{3,}|^:|^(css|sc|jsx|emotion|svelte)-|[_-](?=[a-z0-9]*\d)[a-z0-9]{5,}$/i;

function cssEscape(value: string): string {
  return typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(value) : value.replace(/[^a-zA-Z0-9_-]/g, (c) => `\\${c}`);
}

function isUnique(selector: string, root: ParentNode): boolean {
  try {
    return root.querySelectorAll(selector).length === 1;
  } catch {
    return false;
  }
}

function segment(el: Element): string {
  const tag = el.tagName.toLowerCase();
  const classes = [...el.classList].filter((c) => !UNSTABLE.test(c)).slice(0, 2);
  let part = tag + classes.map((c) => `.${cssEscape(c)}`).join('');
  const parent = el.parentElement;
  if (parent) {
    const sameTag = [...parent.children].filter((c) => c.tagName === el.tagName);
    if (sameTag.length > 1) part += `:nth-of-type(${sameTag.indexOf(el) + 1})`;
  }
  return part;
}

/** Builds a reasonably stable, unique CSS selector for the element. */
export function cssSelector(el: Element): string {
  const root = el.ownerDocument;
  if (el.id && !UNSTABLE.test(el.id) && isUnique(`#${cssEscape(el.id)}`, root)) return `#${cssEscape(el.id)}`;

  const parts: string[] = [];
  let current: Element | null = el;
  while (current && current !== root.documentElement && parts.length < 6) {
    for (const attr of TEST_ATTRS) {
      const value = current.getAttribute(attr);
      if (!value || value.length > 80) continue;
      const candidate = `${current.tagName.toLowerCase()}[${attr}="${value.replace(/"/g, '\\"')}"]`;
      const full = [candidate, ...parts].join(' > ');
      if (isUnique(full, root)) return full;
    }
    if (current !== el && current.id && !UNSTABLE.test(current.id)) {
      const full = [`#${cssEscape(current.id)}`, ...parts].join(' > ');
      if (isUnique(full, root)) return full;
    }
    parts.unshift(segment(current));
    const full = parts.join(' > ');
    if (isUnique(full, root)) return full;
    current = current.parentElement;
  }
  return parts.join(' > ');
}

const IMPLICIT_ROLES: Record<string, string> = {
  a: 'link',
  button: 'button',
  select: 'combobox',
  textarea: 'textbox',
  img: 'img',
  nav: 'navigation',
  table: 'table',
  form: 'form',
  dialog: 'dialog',
  h1: 'heading',
  h2: 'heading',
  h3: 'heading',
  h4: 'heading',
};

function role(el: Element): string | undefined {
  const explicit = el.getAttribute('role');
  if (explicit) return explicit;
  const tag = el.tagName.toLowerCase();
  if (tag === 'input') {
    const type = (el as HTMLInputElement).type;
    if (type === 'checkbox' || type === 'radio') return type;
    if (type === 'submit' || type === 'button') return 'button';
    return 'textbox';
  }
  return IMPLICIT_ROLES[tag];
}

function squash(text: string | null | undefined, max = 80): string | undefined {
  const value = (text ?? '').replace(/\s+/g, ' ').trim();
  if (!value) return undefined;
  return value.length > max ? value.slice(0, max - 1) + '…' : value;
}

/** Accessible-name-ish label. Never reads the value of form fields (privacy). */
function label(el: Element): string | undefined {
  const aria = el.getAttribute('aria-label');
  if (aria) return squash(aria);
  const labelledBy = el.getAttribute('aria-labelledby');
  if (labelledBy) {
    const text = labelledBy
      .split(/\s+/)
      .map((id) => el.ownerDocument.getElementById(id)?.textContent ?? '')
      .join(' ');
    if (squash(text)) return squash(text);
  }
  if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) {
    const fieldLabel = el.labels?.[0]?.textContent;
    return squash(fieldLabel) ?? squash(el.getAttribute('placeholder')) ?? squash(el.getAttribute('name'));
  }
  for (const attr of ['alt', 'title']) {
    const value = el.getAttribute(attr);
    if (value) return squash(value);
  }
  return squash((el as HTMLElement).innerText ?? el.textContent);
}

/** Opening tag without values, styles or long attributes – enough to recognise the element. */
function openingTag(el: Element): string {
  const attrs = [...el.attributes]
    .filter((a) => !['value', 'style', 'srcdoc'].includes(a.name) && !a.name.startsWith('on'))
    .map((a) => (a.value.length > 60 ? `${a.name}="${a.value.slice(0, 57)}…"` : `${a.name}="${a.value}"`));
  const html = `<${el.tagName.toLowerCase()}${attrs.length ? ' ' + attrs.join(' ') : ''}>`;
  return html.length > 300 ? html.slice(0, 299) + '…' : html;
}

export function describeElement(el: Element): MarkedElement {
  const rect = el.getBoundingClientRect();
  return {
    selector: cssSelector(el),
    tag: el.tagName.toLowerCase(),
    role: role(el),
    label: label(el),
    html: openingTag(el),
    rect: {
      x: Math.round(rect.left + window.scrollX),
      y: Math.round(rect.top + window.scrollY),
      width: Math.round(rect.width),
      height: Math.round(rect.height),
    },
  };
}
